import type { AttachedFile } from "@cohesive/api-client";
import { Check, Loader2, X } from "lucide-react";

/**
 * Compact upload progress rows for the unscoped mobile home, rendered in the
 * browser's leading slot. The scoped surface gets real `UploadingCard` ghosts
 * from the workspace queue instead; this strip covers the target-sheet flow,
 * whose uploads may land in a canvas (no corresponding browser row at all).
 * Callers filter out `processed` entries — the finished file's real row shows
 * up in the browser via query invalidation, so a lingering "Done" row here
 * would just duplicate it.
 */
export function MobileUploadStatusStrip({ files }: { files: AttachedFile[] }) {
  if (files.length === 0) return null;
  return (
    <ul className="flex flex-col gap-1 pb-2">
      {files.map((file) => (
        <li
          key={file.id}
          className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm"
        >
          <UploadStatusIcon status={file.status} />
          <span className="flex-1 truncate">{file.name}</span>
          <span className="shrink-0 text-xs text-muted-foreground">
            {uploadStatusLabel(file.status)}
          </span>
        </li>
      ))}
    </ul>
  );
}

function UploadStatusIcon({ status }: { status: string }) {
  if (status === "failed") return <X className="h-4 w-4 text-lc-blocked" />;
  if (status === "processed")
    return <Check className="h-4 w-4 text-lc-active" />;
  return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
}

function uploadStatusLabel(status: string): string {
  switch (status) {
    case "failed":
      return "Failed";
    case "processed":
      return "Done";
    case "processing":
      return "Processing";
    default:
      return "Uploading";
  }
}

export default MobileUploadStatusStrip;
